// Package "ladder": the physical units one vendor product can be counted in,
// and the structured interpretation of its pack description.
//
// packageToCountUnitQty (purchaseConversion.mjs) answers ONE question — how
// many count units are in a package — which is all ordering needs. Counting
// needs more. A cook holding a Sysco case of 24 × 6 fl oz bottles may have two
// full cases and eight loose bottles, and "8 bottles" is only meaningful if we
// know a bottle is 6 fl oz.
//
// So this module decomposes a purchase option into LEVELS:
//
//   case    → 144 fl oz   (the order package)
//   bottle  →   6 fl oz   (the inner pack)
//   fl oz   →   1 fl oz   (the canonical count unit)
//
// Everything here is deterministic and derived from data the store already
// has: purchase_unit, pack_description, qty_per_package, and the item's count
// unit, parsed by the Recipe Lab engine. No AI, no guessed sizes. When a level
// cannot be derived it is simply absent — never present with a zero or an
// invented quantity.
//
// A stored conversion_snapshot.package_interpretation (written once a human
// confirms an interpretation) always wins over re-derivation, so a confirmed
// package never silently changes because a parser improved.
//
// Pure ESM, no Deno APIs: edge functions, the Node test runner, and (through
// the client mirror) the browser all share it.

import {
  normalizeUnit,
  parsePackSize,
  convertToBase,
} from "../menu-recipe-costing/units.mjs";
import { packageToCountUnitQty } from "./purchaseConversion.mjs";
import { canonicalPackWord, readPack, isContainerWord } from "./packText.mjs";

const ENGINE = "menu-recipe-costing/units.mjs";

/** Levels a quantity can be entered at, coarsest first. */
export const LADDER_LEVELS = Object.freeze({
  PACKAGE: "package",
  INNER_PACK: "inner_pack",
  CANONICAL: "canonical",
});

/**
 * entry_kind values for count_item_quantity_entries. They describe HOW the
 * quantity was counted, which the normalized total alone cannot express.
 */
export const ENTRY_KINDS = Object.freeze([
  "canonical_measure", // "2.5 gal" in the item's own unit
  "full_package", //     "1 Costco tub"
  "loose_inner_pack", //  "8 loose bottles"
  "partial_package", //   "0.5 of a tub" — measurable packages only
  "manual_measure", //    a measure in some other compatible unit
]);

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const text = (v) => (v == null ? "" : String(v).trim());

const isMeasurable = (norm) =>
  !!norm && (norm.kind === "mass" || norm.kind === "volume" || norm.kind === "count");

/**
 * The count unit as a container word ("Cases" → "case"), or null when it is
 * not a container at all.
 *
 * A shelf counted in CASES has no canonical base — but it still has a ladder.
 * Refusing it left a case-counted item with no rungs at all: not the package,
 * not the inner pack, and not even its own count unit, so there was no way to
 * enter "3 cases" from the count card.
 */
function containerCountWord(countUnit) {
  const canonical = canonicalPackWord(countUnit);
  if (!canonical) return null;
  // One container vocabulary: the engine's normalizeUnit list PLUS packText's
  // extra words (keg, pouch, crate, drum…). The two lists diverging meant an
  // item counted in kegs fell to unknown_unit while packText happily read
  // "2 kegs × 5 gal" — the same word, container in one classifier, mystery
  // in the other.
  if (normalizeUnit(canonical).kind === "container") return canonical;
  return isContainerWord(canonical) ? canonical : null;
}

/**
 * The level BELOW a container count unit: the loose things a cook can count
 * without a scale when they are holding part of a case.
 *
 * "4 bags × 12 bunches" in a case → 4 bags. "88 apples" → 88 apples. A pack
 * measured by weight ("40 lb") gets none: counting 12 loose POUNDS of a
 * case-counted item needs a scale, which is not what this ladder is for.
 */
function containerInnerLevel(option, countWord) {
  const pack = readPack(option?.pack_description);
  if (!pack.read) return null;

  // The outer wrapper, when the pack names one that is not the count unit.
  if (pack.containerWord && pack.containerWord !== countWord && pack.packCount > 1) {
    return {
      count: pack.packCount,
      noun: pack.containerWord,
      perInner: pack.unitQty,
      unit: pack.pieceWord ?? pack.unitNorm?.key ?? null,
    };
  }
  // A stated piece count IS the level below: 88 apples to the case.
  if (pack.pieceWord && pack.pieceWord !== countWord && pack.totalQty > 1) {
    return { count: pack.totalQty, noun: pack.pieceWord, perInner: 1, unit: pack.pieceWord };
  }
  return null;
}

/**
 * A package is divisible when its contents are a continuous measure, or when
 * the item itself is counted in that same container unit. A case-counted shelf
 * may hold 0.5 case even when the label also tells us that a full case contains
 * 24 bottles. On a bottle- or fl-oz-counted shelf, the same opened case stays
 * expressed at the bottle rung so the package conversion remains auditable.
 */
function packageIsDivisible(pack, orderUnit, countUnit) {
  const orderWord = canonicalPackWord(orderUnit);
  const countWord = containerCountWord(countUnit);
  if (orderWord && countWord && orderWord === countWord) return true;
  if (!pack || !pack.parsed || !isMeasurable(pack.unitNorm)) return false;
  if (pack.unitNorm.kind === "count") return false;
  return pack.packCount === 1;
}

/**
 * Converts `qty` of `unitRaw` into the item's count unit, retrying an
 * ambiguous "oz" as fluid ounces for a volumetric item — the same rule
 * purchaseConversion uses, so the two can never disagree.
 */
function intoCountUnit(qty, unitRaw, unitNorm, countNorm) {
  const attempt = (u) => convertToBase(qty, u, countNorm.base, {});
  let res = attempt(unitRaw);
  let warnings = res.warnings || [];
  if (!res.ok && res.reason === "needs_density" && unitNorm?.ambiguousOz && countNorm.base === "ml") {
    const asFluid = attempt("floz");
    if (asFluid.ok) {
      res = asFluid;
      warnings = [
        ...(asFluid.warnings || []),
        "Interpreted 'oz' as fluid ounces because the count unit is volumetric — verify against the product.",
      ];
    }
  }
  if (!res.ok) return { ok: false, qty: null, warnings };
  return { ok: true, qty: res.qty / countNorm.factor, warnings };
}

/**
 * A previously confirmed interpretation stored on the option, if any.
 * Shape mirrors interpretPackage()'s return so callers can use it directly.
 */
export function storedInterpretation(option) {
  const snap = option?.conversion_snapshot?.package_interpretation;
  if (!snap || typeof snap !== "object") return null;
  // Only a HUMAN-confirmed interpretation may short-circuit re-derivation.
  // This used to accept any snapshot and rebrand it "confirmed", which meant
  // a parser's old opinion could never be improved by a better parser — the
  // exact opposite of the protection confirmations exist to give.
  if (snap.source !== "confirmed" && !snap.confirmed_at) return null;
  let total = positive(snap.quantity_in_item_count_unit);
  if (total === null) {
    // total_package_quantity is stated in the PACKAGE's unit; it only stands
    // in for the count-unit total when the two units actually agree —
    // substituting 144 (fl oz) for a gallon-counted item was the bug.
    const packageUnit = String(snap.total_package_unit ?? "").trim().toLowerCase();
    const countUnit = String(snap.item_count_unit ?? "").trim().toLowerCase();
    if (packageUnit && countUnit && packageUnit === countUnit) {
      total = positive(snap.total_package_quantity);
    }
  }
  if (total === null) return null;
  return { ...snap, quantity_in_item_count_unit: total, source: "confirmed" };
}

/**
 * Structured interpretation of one vendor package, derived deterministically.
 *
 * Answers the §9 schema shape: what the order unit is, how many inner packs it
 * holds, how big each inner pack is, and the total in the item's count unit.
 *
 * A confirmed interpretation on the option short-circuits everything.
 *
 * @param {object} option   purchase_options row
 * @param {string} countUnit the item's canonical count unit
 * @returns {{
 *   order_unit: string|null, order_increment: number,
 *   inner_pack_count: number|null, inner_pack_unit: string|null,
 *   quantity_per_inner_pack: number|null, quantity_unit: string|null,
 *   total_package_quantity: number|null, total_package_unit: string|null,
 *   quantity_in_item_count_unit: number|null, item_count_unit: string|null,
 *   measurement_dimension: 'mass'|'volume'|'count'|null,
 *   confidence: number, requires_review: boolean,
 *   source: 'confirmed'|'derived'|'stored_qty'|'unresolved',
 *   warnings: string[], reasoning: string
 * }}
 */
export function interpretPackage(option, countUnit) {
  const confirmed = storedInterpretation(option);
  if (confirmed) return confirmed;

  const warnings = [];
  const countNorm = normalizeUnit(countUnit);
  const orderUnit = text(option?.purchase_unit) || null;
  const packDesc = text(option?.pack_description);

  const base = {
    order_unit: orderUnit,
    order_increment: 1,
    inner_pack_count: null,
    inner_pack_unit: null,
    quantity_per_inner_pack: null,
    quantity_unit: null,
    total_package_quantity: null,
    total_package_unit: null,
    quantity_in_item_count_unit: null,
    item_count_unit: countNorm.key || (countUnit ?? null),
    measurement_dimension: isMeasurable(countNorm) ? countNorm.kind : null,
    confidence: 0,
    requires_review: true,
    source: "unresolved",
    warnings,
    reasoning: "",
  };

  if (!isMeasurable(countNorm)) {
    // ── The shelf counts a CONTAINER ────────────────────────────────────────
    // Container arithmetic answers the total (packageToCountUnitQty path 0)
    // and the pack text supplies the level below, so a cook holding a broken
    // case enters "1 case and 12 apples" instead of guessing a fraction.
    // Contents are still never sized: a container we cannot count stays
    // unresolved.
    const countWord = containerCountWord(countUnit);
    if (!countWord) {
      warnings.push(`Count unit "${countUnit ?? ""}" is not measurable, so packages cannot be decomposed.`);
      return base;
    }
    const conv = packageToCountUnitQty({
      packDescription: option?.pack_description,
      purchaseUnit: option?.purchase_unit,
      countUnit,
      caseQuantity: option?.case_quantity,
    });
    warnings.push(...(conv.warnings || []));
    const total = conv.status === "valid" ? positive(conv.qty) : positive(option?.qty_per_package);
    if (total === null) {
      warnings.push(`The package does not say how many ${countWord}(s) it holds.`);
      return base;
    }
    const inner = containerInnerLevel(option, countWord);
    return {
      ...base,
      item_count_unit: countWord,
      inner_pack_count: inner?.count ?? null,
      inner_pack_unit: inner?.noun ?? null,
      quantity_per_inner_pack: inner?.perInner ?? null,
      quantity_unit: inner?.unit ?? null,
      total_package_quantity: round6(total),
      total_package_unit: countWord,
      quantity_in_item_count_unit: round6(total),
      // A wrapper is not a measurement dimension — nothing here was weighed.
      measurement_dimension: null,
      confidence: conv.status === "valid" ? 0.7 : 0.5,
      requires_review: conv.status !== "valid",
      source: conv.status === "valid" ? "derived" : "stored_qty",
      warnings,
      reasoning: inner
        ? `One ${orderUnit || "package"} is ${round6(total)} ${countWord}, holding ${inner.count} ${inner.noun}.`
        : `One ${orderUnit || "package"} is ${round6(total)} ${countWord}.`,
    };
  }

  const pack = packDesc ? parsePackSize(packDesc) : null;

  // Inner-pack decomposition: "24/6 fl oz" is 24 bottles of 6 fl oz. packCount
  // > 1 is what makes an inner level exist at all; "5 gal" (packCount 1) is a
  // single container with no inner pack.
  if (pack && pack.parsed && isMeasurable(pack.unitNorm) && !pack.special) {
    const innerQty = positive(pack.unitQty);
    const innerCount = positive(pack.packCount);
    if (innerQty !== null && innerCount !== null) {
      const totalRaw = innerCount * innerQty;
      const conv = intoCountUnit(totalRaw, pack.unitRaw, pack.unitNorm, countNorm);
      warnings.push(...conv.warnings);
      if (conv.ok && conv.qty > 0) {
        const hasInner = innerCount > 1;
        return {
          ...base,
          inner_pack_count: hasInner ? innerCount : null,
          inner_pack_unit: hasInner ? innerPackNoun(option, pack) : null,
          quantity_per_inner_pack: hasInner ? innerQty : null,
          quantity_unit: pack.unitNorm.key,
          total_package_quantity: round6(totalRaw),
          total_package_unit: pack.unitNorm.key,
          quantity_in_item_count_unit: round6(conv.qty),
          measurement_dimension: pack.unitNorm.kind,
          confidence: pack.confidence ?? 0.8,
          requires_review: (pack.confidence ?? 0.8) < 0.8 || conv.warnings.length > 0,
          source: "derived",
          warnings,
          reasoning: hasInner
            ? `One ${orderUnit || "package"} contains ${innerCount} × ${innerQty} ${pack.unitNorm.key} = ${round6(totalRaw)} ${pack.unitNorm.key}.`
            : `One ${orderUnit || "package"} contains ${round6(totalRaw)} ${pack.unitNorm.key}.`,
        };
      }

      // The total does not convert (240 OZ has no bridge into EA) but the
      // pack states its piece count outright: "20/12 OZ" on an EA-counted
      // item is twenty pieces of twelve ounces. Both facts are kept — the
      // count answers the order question, the per-piece size stays visible.
      if (countNorm.kind === "count" && pack.packCountExplicit && innerCount >= 1
        && (pack.unitNorm.kind === "mass" || pack.unitNorm.kind === "volume")) {
        const qtyInCount = round6(innerCount / countNorm.factor);
        const sizeLabel = pack.unitQtyMin != null && pack.unitQtyMax != null
          ? `${pack.unitQtyMin}–${pack.unitQtyMax} ${pack.unitNorm.key}${pack.approximate ? "" : ""}`
          : `${innerQty} ${pack.unitNorm.key}${pack.approximate ? " avg" : ""}`;
        return {
          ...base,
          inner_pack_count: innerCount,
          inner_pack_unit: innerPackNoun(option, pack),
          quantity_per_inner_pack: innerQty,
          quantity_unit: pack.unitNorm.key,
          total_package_quantity: innerCount,
          total_package_unit: countNorm.key,
          quantity_in_item_count_unit: qtyInCount,
          measurement_dimension: "count",
          confidence: 0.9,
          requires_review: false,
          source: "derived",
          warnings,
          reasoning: `One ${orderUnit || "package"} holds ${innerCount} pieces of ${sizeLabel} each — ${qtyInCount} ${countNorm.key}.`,
        };
      }
    }
  }

  // No usable pack text. Fall back to the conversion the ordering path already
  // trusts, which knows the direct and count-package routes too.
  const conv = packageToCountUnitQty({
    packDescription: option?.pack_description,
    purchaseUnit: option?.purchase_unit,
    countUnit,
    caseQuantity: option?.case_quantity,
  });
  warnings.push(...(conv.warnings || []));

  // A `fallback_each` DEFAULT of 1 is a counting convenience, not a derivation.
  // Reporting it as a reviewed-and-derived quantity is what let an unreadable
  // pack order six cases while a readable one asked for confirmation.
  const convUsable = conv.status === "valid" && conv.orderSafe !== false;

  const storedQty = positive(option?.qty_per_package);
  const qty = convUsable ? conv.qty : storedQty;
  if (qty === null) {
    if (pack?.special === "number_can") {
      warnings.push("#-can sizing does not state net contents — confirm the package size.");
    }
    if (conv.status === "valid" && conv.orderSafe === false) {
      warnings.push("Package contents defaulted to 1 with no stated case quantity — confirm the package before it can drive an order.");
    }
    return base;
  }

  return {
    ...base,
    total_package_quantity: round6(qty),
    total_package_unit: countNorm.key,
    quantity_in_item_count_unit: round6(qty),
    confidence: convUsable ? 0.7 : 0.5,
    // A quantity taken from the stored column has no derivation behind it.
    requires_review: !convUsable,
    source: convUsable ? "derived" : "stored_qty",
    warnings,
    reasoning: convUsable
      ? `One ${orderUnit || "package"} is ${round6(qty)} ${countNorm.key} (${conv.provenance?.method ?? "derived"}).`
      : `Using the stored quantity per package: ${round6(qty)} ${countNorm.key}.`,
  };
}

/**
 * What to call one inner pack. The vendor's own wording is best when it names
 * a container ("BTLS", "bag"); otherwise fall back to the parsed unit.
 */
function innerPackNoun(option, pack) {
  const desc = `${text(option?.pack_description)} ${text(option?.vendor_description)}`.toLowerCase();
  const nouns = ["bottle", "btl", "can", "jar", "bag", "box", "tub", "jug", "pouch", "tray", "sleeve", "carton", "tin"];
  const hit = nouns.find((n) => desc.includes(n));
  if (hit) return hit === "btl" ? "bottle" : hit;
  return pack?.unitNorm?.key || "unit";
}

/**
 * The levels this option can be counted at, coarsest first.
 *
 * Each rung carries the quantity ONE of it represents in the item's count
 * unit, so the caller multiplies and sums without knowing any unit maths.
 * A rung whose quantity cannot be derived is omitted entirely — the caller
 * cannot then accidentally treat it as zero.
 *
 * @returns {Array<{key, level, label, unitLabel, qtyInCountUnit,
 *                  allowsFractional, optionId, entryKind}>}
 */
export function buildOptionLadder(option, countUnit, vendorLabel) {
  const rungs = [];
  if (!option) return rungs;
  const interp = interpretPackage(option, countUnit);
  const optionId = option.id ?? null;
  const pack = text(option.pack_description) ? parsePackSize(text(option.pack_description)) : null;

  const packageQty = positive(interp.quantity_in_item_count_unit);
  if (packageQty !== null) {
    const orderUnit = interp.order_unit || "package";
    rungs.push({
      key: `${optionId}:package`,
      level: LADDER_LEVELS.PACKAGE,
      optionId,
      entryKind: "full_package",
      label: vendorLabel ? `${vendorLabel} — ${orderUnit}` : orderUnit,
      unitLabel: orderUnit,
      detail: text(option.pack_description) || null,
      qtyInCountUnit: packageQty,
      // A same-unit case may be counted as 0.5 case; a case viewed through a
      // bottle/floz shelf still uses its discrete inner rung.
      allowsFractional: packageIsDivisible(pack, orderUnit, countUnit),
    });
  }

  const innerQty = positive(interp.quantity_per_inner_pack);
  const innerCount = positive(interp.inner_pack_count);
  if (innerQty !== null && innerCount !== null && packageQty !== null) {
    // Derive the inner rung from the PACKAGE total so the two rungs can never
    // disagree: N inner packs must add up to exactly one package.
    const perInner = round6(packageQty / innerCount);
    if (perInner > 0) {
      const noun = interp.inner_pack_unit || "unit";
      rungs.push({
        key: `${optionId}:inner`,
        level: LADDER_LEVELS.INNER_PACK,
        optionId,
        entryKind: "loose_inner_pack",
        label: vendorLabel ? `${vendorLabel} — ${noun}` : noun,
        unitLabel: noun,
        detail: `${innerQty} ${interp.quantity_unit || ""}`.trim() || null,
        qtyInCountUnit: perInner,
        allowsFractional: false,
      });
    }
  }

  return rungs;
}

/**
 * Every way this item can be counted: one rung per canonical unit, plus each
 * active option's package and inner-pack rungs.
 *
 * `unresolved` lists options whose package could not be decomposed, so the UI
 * can show them under "Needs unit review" instead of pretending they are
 * absent.
 *
 * @param {object} args
 * @param {string} args.countUnit           the item's canonical count unit
 * @param {Array}  args.options             purchase_options rows
 * @param {Object} [args.vendorNamesById]   { [vendorId]: display_name }
 * @returns {{ rungs: Array, unresolved: Array<{optionId, vendorLabel, reason}> }}
 */
export function buildCountLadder({ countUnit, options, vendorNamesById } = {}) {
  const countNorm = normalizeUnit(countUnit);
  const rungs = [];
  const unresolved = [];

  // The item's own count unit is always a way to count it — including when it
  // is a container. A case-counted shelf had no canonical rung at all, so
  // "3 cases" was not enterable from the count card.
  const containerWord = isMeasurable(countNorm) ? null : containerCountWord(countUnit);
  if (isMeasurable(countNorm) || containerWord) {
    const label = containerWord || countNorm.key;
    rungs.push({
      key: "canonical",
      level: LADDER_LEVELS.CANONICAL,
      optionId: null,
      entryKind: "canonical_measure",
      label,
      unitLabel: label,
      detail: null,
      qtyInCountUnit: 1,
      // A fraction is on-hand inventory, not permission to order a fraction.
      // A case-counted item can therefore hold 0.5 case while purchasing still
      // rounds through the vendor option's MOQ and order increment.
      allowsFractional: !!containerWord || countNorm.kind !== "count",
    });
  }

  const names = vendorNamesById || {};
  (options || []).forEach((option) => {
    if (!option || option.status !== "active") return;
    const vendorLabel =
      text(names[option.vendor_id]) || text(option.vendor_name_text) || null;
    const optionRungs = buildOptionLadder(option, countUnit, vendorLabel);
    if (optionRungs.length === 0) {
      const interp = interpretPackage(option, countUnit);
      unresolved.push({
        optionId: option.id ?? null,
        vendorLabel,
        packDescription: text(option.pack_description) || null,
        reason: interp.warnings[0] || "The package size could not be determined.",
      });
      return;
    }
    rungs.push(...optionRungs);
  });

  return { rungs, unresolved };
}

/**
 * Recomputes a model's package interpretation from scratch and accepts it only
 * where it agrees with deterministic arithmetic.
 *
 * The model may read language — "MAYO GLASS 24/6" means 24 bottles of 6 — but
 * it never owns the numbers. Every quantity is recomputed here, units are
 * re-normalized, dimensions re-checked, and the option id is validated against
 * the list actually supplied. Anything that fails comes back null, never zero.
 *
 * A piece-count answer is accepted for count-family count units: when the item
 * counts EA/CT/PC and the proposal carries a stated piece count (or an inner
 * pack count with a mass/volume per-piece size), the quantity in the count
 * unit IS that piece count — "240 OZ does not convert into EA" is true and
 * beside the point when the pack says "20/12 OZ". Mirrors the deterministic
 * pack_piece_count path (purchaseConversion) so the two layers agree.
 *
 * @param {object} proposal        raw model output
 * @param {object} args
 * @param {Array}  args.options    the options the model was allowed to see
 * @param {string} args.countUnit
 * @returns {{ ok: boolean, interpretation: object|null, errors: string[] }}
 */
export function validatePackageInterpretation(proposal, { options, countUnit } = {}) {
  const errors = [];
  const fail = (msg) => {
    errors.push(msg);
    return { ok: false, interpretation: null, errors };
  };

  if (!proposal || typeof proposal !== "object") return fail("No interpretation returned.");

  const allowed = (options || []).filter(Boolean);
  const optionRef = proposal.option_ref ?? proposal.optionId ?? proposal.option_id ?? null;
  const option = allowed.find((o) => String(o.id) === String(optionRef));
  if (!option) return fail(`Interpretation references an unknown purchase option (${optionRef}).`);

  const countNorm = normalizeUnit(countUnit);
  const countContainer = isMeasurable(countNorm) ? null : containerCountWord(countUnit);
  if (!isMeasurable(countNorm) && !countContainer) {
    return fail(`Count unit "${countUnit ?? ""}" is neither measurable nor a recognized package unit.`);
  }

  const innerCount = positive(proposal.inner_pack_count);
  const perInner = positive(proposal.quantity_per_inner_pack) ?? positive(proposal.explicit_per_piece_quantity);
  const quantityUnit = text(proposal.quantity_unit) || text(proposal.explicit_per_piece_unit)
    || text(proposal.total_package_unit) || text(proposal.stated_total_unit);
  const quantityNorm = normalizeUnit(quantityUnit);

  const rawConfidence = Number(proposal.confidence);
  const confidence = Number.isFinite(rawConfidence) ? Math.min(1, Math.max(0, rawConfidence)) : 0.5;
  const assumptions = Array.isArray(proposal.assumptions) ? proposal.assumptions.filter(Boolean) : [];
  const averageIndicator = proposal.average_indicator === true;

  // The model's reading of the TEXT, passed through on every acceptance so
  // review sees what the words said, not only the recomputed arithmetic.
  const passThrough = {
    explicit_piece_count: positive(proposal.explicit_piece_count),
    explicit_per_piece_quantity: positive(proposal.explicit_per_piece_quantity),
    explicit_per_piece_unit: text(proposal.explicit_per_piece_unit) || null,
    per_piece_range_min: positive(proposal.per_piece_range_min),
    per_piece_range_max: positive(proposal.per_piece_range_max),
    average_indicator: averageIndicator,
    stated_total_quantity: positive(proposal.stated_total_quantity),
    stated_total_unit: text(proposal.stated_total_unit) || null,
    evidence: text(proposal.evidence) || null,
  };

  // A restaurant may count the physical inner package on the shelf (PK, bag,
  // bottle) while buying an outer case. The measured size and the package
  // count are simultaneous facts: 6/12 OZ counted in PK means six packs for
  // ordering AND 72 oz in the case. A mass-to-container conversion is not
  // attempted; the semantic mapping of `inner_pack_unit` to the count unit is
  // what makes the count valid.
  if (countContainer) {
    const innerUnit = canonicalPackWord(proposal.inner_pack_unit);
    if (innerCount === null) {
      return fail(`The interpretation does not state how many ${countContainer}s are in the package.`);
    }
    if (!innerUnit || innerUnit !== countContainer) {
      return fail(`Inner package unit "${proposal.inner_pack_unit ?? ""}" does not match the item count unit "${countContainer}".`);
    }
    if (perInner !== null && !isMeasurable(quantityNorm)) {
      return fail(`Per-${countContainer} unit "${quantityUnit}" is not measurable.`);
    }

    const warnings = [];
    if (passThrough.explicit_piece_count !== null
      && Math.abs(passThrough.explicit_piece_count - innerCount) > 1e-9) {
      warnings.push(
        `The stated inner count (${passThrough.explicit_piece_count}) and interpreted ${countContainer} count (${innerCount}) disagree.`,
      );
    }
    const measuredTotal = perInner !== null && isMeasurable(quantityNorm)
      ? round6(innerCount * perInner)
      : null;
    const confidenceNeedsReview = confidence < 0.8;
    const requiresReview = proposal.requires_review === true
      || averageIndicator
      || assumptions.length > 0
      || confidenceNeedsReview
      || warnings.length > 0;
    const orderUnit = text(proposal.order_unit) || text(option.purchase_unit) || null;
    return {
      ok: true,
      errors,
      interpretation: {
        option_ref: option.id,
        outer_order_unit: orderUnit,
        order_unit: orderUnit,
        order_increment: positive(proposal.order_increment) ?? 1,
        inner_pack_count: innerCount,
        inner_pack_unit: countContainer,
        inner_levels: [{
          count: innerCount,
          unit_type: countContainer,
          unit_raw: text(proposal.inner_pack_unit) || countContainer,
          evidence: passThrough.evidence,
          confidence,
        }],
        quantity_per_inner_pack: perInner,
        quantity_unit: isMeasurable(quantityNorm) ? quantityNorm.key : null,
        per_inner_measurements: perInner !== null && isMeasurable(quantityNorm)
          ? [{ quantity: perInner, unit: quantityNorm.key, evidence: passThrough.evidence, confidence }]
          : [],
        total_package_quantity: innerCount,
        total_package_unit: countContainer,
        measured_total_quantity: measuredTotal,
        measured_total_unit: measuredTotal !== null ? quantityNorm.key : null,
        stated_total_measurements: passThrough.stated_total_quantity !== null
          ? [{ quantity: passThrough.stated_total_quantity, unit: passThrough.stated_total_unit, evidence: passThrough.evidence, confidence }]
          : [],
        quantity_in_item_count_unit: innerCount,
        item_count_unit: countContainer,
        measurement_dimension: "count",
        confidence,
        reasoning: text(proposal.reasoning) || "",
        assumptions,
        requires_review: requiresReview,
        warnings,
        source: "ai_proposed",
        engine: ENGINE,
        ...passThrough,
      },
    };
  }

  // Pieces the proposal states, usable only on a count-family count unit:
  // an explicit piece count outright, or an inner pack count whose per-piece
  // size is a mass/volume (each piece is one countable thing).
  const pieces = countNorm.kind === "count"
    ? passThrough.explicit_piece_count
      ?? (innerCount !== null && (quantityNorm.kind === "mass" || quantityNorm.kind === "volume")
        ? innerCount
        : null)
    : null;

  const acceptPieceCount = () => {
    const warnings = [];
    if (passThrough.explicit_piece_count !== null && innerCount !== null
      && Math.abs(passThrough.explicit_piece_count - innerCount) > 1e-9) {
      warnings.push(
        `The stated piece count (${passThrough.explicit_piece_count}) and inner pack count (${innerCount}) disagree; used the stated piece count.`,
      );
    }
    const qtyInCount = round6(pieces / countNorm.factor);
    // The per-piece size rides along as inner-pack structure when it is a real
    // measure; the COUNT is the answer either way.
    const perPieceMeasured = isMeasurable(quantityNorm) && quantityNorm.kind !== "count";
    return {
      ok: true,
      errors,
      interpretation: {
        option_ref: option.id,
        order_unit: text(proposal.order_unit) || text(option.purchase_unit) || null,
        order_increment: positive(proposal.order_increment) ?? 1,
        inner_pack_count: innerCount ?? pieces,
        inner_pack_unit: text(proposal.inner_pack_unit) || null,
        quantity_per_inner_pack: perPieceMeasured ? perInner : null,
        quantity_unit: perPieceMeasured ? quantityNorm.key : null,
        total_package_quantity: round6(pieces),
        total_package_unit: countNorm.key,
        quantity_in_item_count_unit: qtyInCount,
        item_count_unit: countNorm.key,
        measurement_dimension: "count",
        confidence,
        reasoning: text(proposal.reasoning) || "",
        assumptions,
        // A nominal/average count may drive math, but a human should glance at
        // it before it drives orders — exactness is the vendor's claim.
        requires_review:
          proposal.requires_review === true ||
          averageIndicator ||
          assumptions.length > 0 ||
          confidence < 0.8 ||
          warnings.length > 0,
        warnings,
        source: "ai_proposed",
        engine: ENGINE,
        ...passThrough,
      },
    };
  };

  if (!isMeasurable(quantityNorm)) {
    // An explicit piece count needs no measure at all ("80 CT" states pieces,
    // not ounces) — but only the count-family piece path may say so.
    if (pieces !== null) return acceptPieceCount();
    return fail(`Quantity unit "${quantityUnit}" is not a unit we can convert.`);
  }

  // Recompute the total ourselves rather than trusting total_package_quantity.
  let totalRaw;
  if (innerCount !== null && perInner !== null) {
    totalRaw = innerCount * perInner;
  } else {
    // A stated total may stand in only when its unit agrees with the unit the
    // rest of the proposal is measured in — 50 LB must not be read as 50 OZ.
    const statedUnitText = text(proposal.stated_total_unit);
    const statedCompatible = !statedUnitText || normalizeUnit(statedUnitText).key === quantityNorm.key;
    totalRaw = positive(proposal.total_package_quantity)
      ?? (statedCompatible ? positive(proposal.stated_total_quantity) : null);
    if (totalRaw === null) {
      if (pieces !== null) return acceptPieceCount();
      return fail("No usable package quantity in the interpretation.");
    }
  }

  const conv = intoCountUnit(totalRaw, quantityUnit, quantityNorm, countNorm);
  if (!conv.ok || !(conv.qty > 0)) {
    // The measure does not reach the count unit, but the text states the piece
    // count outright — the count answers the question the measure cannot.
    if (pieces !== null) return acceptPieceCount();
    return fail(`${totalRaw} ${quantityUnit} does not convert into the count unit "${countUnit}".`);
  }

  // A stated total that disagrees with the recomputed one means the model did
  // its own arithmetic and got it wrong; trust ours and say so.
  const warnings = [...conv.warnings];
  const stated = positive(proposal.total_package_quantity);
  if (stated !== null && Math.abs(stated - totalRaw) > Math.max(0.01, totalRaw * 0.001)) {
    warnings.push(
      `The stated package total (${stated}) disagreed with ${innerCount} × ${perInner} = ${round6(totalRaw)}; used the recomputed value.`,
    );
  }

  const claimedDimension = text(proposal.measurement_dimension);
  if (claimedDimension && claimedDimension !== quantityNorm.kind) {
    warnings.push(`Interpretation called this ${claimedDimension}; the unit is ${quantityNorm.kind}.`);
  }

  // Crossing measurement families always needs a human: it can only have been
  // done with an estimated piece weight or density.
  const crossesFamilies = quantityNorm.kind !== countNorm.kind;

  return {
    ok: true,
    errors,
    interpretation: {
      option_ref: option.id,
      order_unit: text(proposal.order_unit) || text(option.purchase_unit) || null,
      order_increment: positive(proposal.order_increment) ?? 1,
      inner_pack_count: innerCount,
      inner_pack_unit: text(proposal.inner_pack_unit) || null,
      quantity_per_inner_pack: perInner,
      quantity_unit: quantityNorm.key,
      total_package_quantity: round6(totalRaw),
      total_package_unit: quantityNorm.key,
      quantity_in_item_count_unit: round6(conv.qty),
      item_count_unit: countNorm.key,
      measurement_dimension: quantityNorm.kind,
      confidence,
      reasoning: text(proposal.reasoning) || "",
      assumptions,
      requires_review:
        proposal.requires_review === true ||
        crossesFamilies ||
        assumptions.length > 0 ||
        confidence < 0.8 ||
        warnings.length > 0,
      warnings,
      source: "ai_proposed",
      engine: ENGINE,
      ...passThrough,
    },
  };
}
